import React, { useEffect, useState } from 'react'
import {
	Box,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
} from '@mui/material'
import { AppButtonIcon, AppCardTitle, AppResultsSkeleton, AppNote } from '@components'
import { MbPage } from 'Pages/MyBusiness/_shared/Ui'

import { AdminProgress, adminCardSx, adminFieldsSx, rowDeleteButtonSx, rowIconButtonSx, rowToneSx } from '../_shared/ui'

/**
 * Module 4 — Category Macros admin (ACRISS classes).
 *
 * Seeded with 11 ACRISS-derived macros (Mini/Economy/Compact/…). Each
 * SIPP is mapped to at most one macro. Migration auto-maps SIPPs by
 * ACRISS first-letter, operator adjusts here.
 */
const API = '/api/my-business/admin'

export default function CategoryMacrosPage({ embedded = false }) {
	const [macros, setMacros] = useState([])
	const [sipps,  setSipps]  = useState([])
	const [loading, setLoading] = useState(true)
	const [error,   setError]   = useState(null)
	const [saving,  setSaving]  = useState(false)

	const [newCode, setNewCode] = useState('')
	const [newName, setNewName] = useState('')

	const reload = () => {
		setLoading(true); setError(null)
		Promise.all([
			fetch(`${API}/category-macros`, { credentials: 'include' })
				.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))),
			fetch(`${API}/sipp-macro-mappings`, { credentials: 'include' })
				.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))),
		]).then(([m, s]) => { setMacros(m); setSipps(s) })
		  .catch(e => setError(e.message))
		  .finally(() => setLoading(false))
	}

	useEffect(() => { reload() }, [])

	const call = (path, opts = {}) => {
		setSaving(true); setError(null)
		return fetch(`${API}${path}`, {
			credentials: 'include',
			headers: opts.body ? { 'Content-Type': 'application/json' } : undefined,
			...opts,
		}).then(r => r.json().then(body => ({ ok: r.ok, status: r.status, body })))
		  .then(({ ok, status, body }) => {
			  if (!ok) throw new Error(body?.error || `HTTP ${status}`)
			  return body
		  })
		  .catch(e => { setError(e.message); throw e })
		  .finally(() => setSaving(false))
	}

	const createMacro = () => {
		const code = newCode.trim(); const name = newName.trim()
		if (!code || !name) return
		call('/category-macros', { method: 'POST', body: JSON.stringify({ code, name }) })
			.then(() => { setNewCode(''); setNewName(''); reload() }).catch(() => {})
	}

	const patchMacro = (id, patch) =>
		call(`/category-macros/${id}`, { method: 'PATCH', body: JSON.stringify(patch) })
			.then(reload).catch(() => {})

	const deleteMacro = (id) => {
		if (!window.confirm('Delete this macro? SIPPs must be reassigned first.')) return
		call(`/category-macros/${id}`, { method: 'DELETE' }).then(reload).catch(() => {})
	}

	const setMapping = (sipp_code, macro_id) =>
		call(`/sipp-macro-mappings/${sipp_code}`,
		     { method: 'PUT', body: JSON.stringify({ macro_id: macro_id || null }) })
			.then(reload).catch(() => {})

	const unmappedCount = sipps.filter(s => !s.macro_id).length

	const body = (
		<Box>
			<AdminProgress active={saving}/>
			{error && <AppNote severity="error" sx={{ mb: 2 }}>{error}</AppNote>}
			{loading && !macros.length ? <AppResultsSkeleton columns={3}/> : <>

				<Paper sx={{ ...adminCardSx, ...adminFieldsSx }}>
					<AppCardTitle icon="layer-group" title="Macros"/>
					<TableContainer sx={{ mt: 1.5 }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell width={100}>Order</TableCell>
									<TableCell width={200}>Code</TableCell>
									<TableCell>Name</TableCell>
									<TableCell width={80}/>
								</TableRow>
							</TableHead>
							<TableBody>
								{macros.map(m => (
									<TableRow key={m.id}>
										<TableCell>
											<TextField type="number" size="small" defaultValue={m.sort_order}
											           onBlur={(e) => {
												           const v = Number(e.target.value)
												           if (v !== m.sort_order) patchMacro(m.id, { sort_order: v })
											           }}
											           inputProps={{ style: { width: 60 } }}/>
										</TableCell>
										<TableCell><code>{m.code}</code></TableCell>
										<TableCell>
											<TextField size="small" fullWidth defaultValue={m.name}
											           onBlur={(e) => {
												           const v = e.target.value.trim()
												           if (v && v !== m.name) patchMacro(m.id, { name: v })
											           }}/>
										</TableCell>
										<TableCell>
											<AppButtonIcon icon="trash" size="small" sx={rowDeleteButtonSx}
											               onClick={() => deleteMacro(m.id)}/>
										</TableCell>
									</TableRow>
								))}
								<TableRow>
									<TableCell/>
									<TableCell>
										<TextField size="small" placeholder="code" fullWidth
										           value={newCode}
										           onChange={(e) => setNewCode(e.target.value)}/>
									</TableCell>
									<TableCell>
										<TextField size="small" placeholder="Display name" fullWidth
										           value={newName}
										           onChange={(e) => setNewName(e.target.value)}/>
									</TableCell>
									<TableCell>
										<AppButtonIcon icon="plus" size="small" sx={rowIconButtonSx}
										               onClick={createMacro}
										               disabled={!newCode.trim() || !newName.trim()}/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>

				<Paper sx={{ ...adminCardSx, ...adminFieldsSx, mb: 0 }}>
					<AppCardTitle icon="car" title={`SIPP → Macro mappings · ${unmappedCount} unassigned`}/>
					<TableContainer sx={{ maxHeight: 500, mt: 1.5 }}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell width={80}>SIPP</TableCell>
									<TableCell>Vehicle class</TableCell>
									<TableCell width={260}>Macro</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{sipps.map(s => (
									<TableRow key={s.sipp_code}
									          sx={rowToneSx({ unassigned: !s.macro_id })}>
										<TableCell><code>{s.sipp_code}</code></TableCell>
										<TableCell>{s.vehicle_class || s.name || '—'}</TableCell>
										<TableCell>
											<Select size="small" fullWidth
											        value={s.macro_id || ''}
											        onChange={(e) => setMapping(s.sipp_code, e.target.value || null)}
											        displayEmpty>
												<MenuItem value=""><em>Unassigned</em></MenuItem>
												{macros.map(m => (
													<MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
												))}
											</Select>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
			</>}
		</Box>
	)

	if (embedded) return body
	return (
		<MbPage title="Category Macros" subtitle="Group SIPPs into ACRISS macro-classes">
			{body}
		</MbPage>
	)
}
